import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function ShakaiVsKokumin() {
  return (
    <GuideLayout
      title="Shakai hoken vs kokumin hoken"
      subtitle="Qual seguro você tem, qual seria melhor, e o que muda quando você troca de emprego ou vira autônomo."
      slug="shakai-vs-kokumin"
      description="Comparação entre shakai hoken e kokumin hoken no Japão. Custos, cobertura, quando cada um se aplica, o que muda na transição."
      updated="Maio de 2026"
      readTime="7 min"
    >
      <P>
        No Japão, todo residente é obrigado a ter seguro de saúde e a
        contribuir para uma aposentadoria. Existem dois sistemas paralelos:{" "}
        <Strong>shakai hoken</Strong> (社会保険), o seguro empresarial, e{" "}
        <Strong>kokumin hoken</Strong> (国民健康保険) somado ao{" "}
        <Em>kokumin nenkin</Em>, para quem não tem empresa.
      </P>
      <P>
        A pergunta &ldquo;qual é melhor?&rdquo; é meio enganosa. Você
        normalmente não escolhe — depende do seu vínculo de trabalho. Mas
        entender a diferença ajuda a evitar surpresas quando você muda de
        emprego, é dispensado, ou pensa em virar autônomo.
      </P>

      <H2 id="quem-tem-cada-um">Quem tem cada um</H2>

      <H3>Shakai hoken (社会保険)</H3>
      <P>Você tem shakai hoken se:</P>
      <UL>
        <li>É empregado formal de uma empresa japonesa (CLT japonês).</li>
        <li>Trabalha 30+ horas por semana ou 75% do horário regular.</li>
        <li>A empresa tem 5+ funcionários (em maioria dos casos).</li>
      </UL>
      <P>
        Pacote único que cobre <Strong>quatro coisas</Strong>: seguro de
        saúde (健康保険), aposentadoria (厚生年金), seguro-desemprego
        (雇用保険) e, a partir dos 40 anos, seguro de cuidados de longa
        duração (介護保険).
      </P>

      <H3>Kokumin hoken (国民健康保険) + kokumin nenkin (国民年金)</H3>
      <P>Você tem kokumin se:</P>
      <UL>
        <li>É autônomo, freelancer, ou tem negócio próprio.</li>
        <li>
          Está desempregado mas registrado no Japão (tem que se inscrever
          mesmo sem renda).
        </li>
        <li>
          Trabalha em contrato parcial que não atinge o mínimo para shakai.
        </li>
        <li>
          É estudante (universidade pode oferecer plano específico).
        </li>
      </UL>
      <P>
        Você paga <Strong>dois carnês separados</Strong>: kokumin kenko hoken
        (saúde) na prefeitura, e kokumin nenkin (aposentadoria) via Japan
        Pension Service.
      </P>

      <H2 id="diferenca-de-custo">A diferença de custo</H2>

      <H3>Quem paga e quanto</H3>
      <Callout>
        <Strong>Shakai hoken:</Strong> a empresa paga metade. Você paga a
        outra metade descontada direto do salário. Total aproximado da sua
        parte: <Strong>14% a 16% do salário bruto</Strong> (depende da idade
        e da prefeitura).
      </Callout>
      <Callout>
        <Strong>Kokumin:</Strong> você paga sozinho. Kokumin nenkin é fixo
        em <Strong>¥17.510/mês</Strong> (valor FY2025, atualizado anualmente
        em abril). Kokumin kenko hoken varia muito por município —
        tipicamente entre <Strong>7% e 13% da renda anual</Strong>, com tetos
        e descontos baseados em dependentes.
      </Callout>

      <H3>Para um exemplo concreto</H3>
      <P>
        Considere um trabalhador com salário bruto anual de ¥3,6 milhões
        (¥300mil/mês):
      </P>
      <UL>
        <li>
          <Strong>No shakai hoken:</Strong> paga aproximadamente
          ¥540.000/ano (¥45mil/mês). A empresa paga outro tanto.
        </li>
        <li>
          <Strong>No kokumin:</Strong> paga ¥210.000 de nenkin (¥17.510 ×
          12) + aproximadamente ¥300.000 de kokumin kenko hoken (varia muito
          por cidade). Total: ~¥510.000/ano (¥42.500/mês). Sem contraparte
          do empregador.
        </li>
      </UL>
      <P>
        Numa olhada superficial parece equivalente. Mas no shakai sua
        aposentadoria acumula muito mais (porque a empresa contribui
        também), e a cobertura de afastamentos é melhor.
      </P>

      <H2 id="cobertura">O que cada um cobre</H2>

      <H3>Cobertura de saúde — praticamente idêntica</H3>
      <P>
        Tanto shakai quanto kokumin pagam <Strong>70% das despesas
        médicas</Strong>. Você paga 30% no balcão (20% para idosos, 10% para
        crianças pequenas em muitas prefeituras). Não há diferença prática
        no atendimento — você usa o mesmo cartão de seguro e os mesmos
        hospitais.
      </P>

      <H3>Aposentadoria — diferença grande</H3>
      <UL>
        <li>
          <Strong>Kosei nenkin (parte do shakai):</Strong> proporcional ao
          salário. Quanto mais você ganha, mais acumula. Aposentadoria
          mensal típica de um trabalhador médio com 40 anos de
          contribuição: <Strong>¥150.000 a ¥220.000</Strong>.
        </li>
        <li>
          <Strong>Kokumin nenkin:</Strong> valor fixo proporcional aos meses
          contribuídos. Máximo (40 anos contribuídos):{" "}
          <Strong>~¥66.000/mês</Strong> em FY2025.
        </li>
      </UL>
      <P>
        Quem só teve kokumin nenkin a vida toda fica com a aposentadoria
        bem menor. Para quem planeja envelhecer no Japão, isso é um fator
        importante.
      </P>

      <H3>Afastamento por doença e desemprego</H3>
      <UL>
        <li>
          <Strong>Shakai hoken</Strong> inclui <Em>shouhei tsumiken</Em>{" "}
          (傷病手当金) — benefício de até 2/3 do salário se você ficar
          doente por 4+ dias consecutivos. Inclui também seguro-desemprego.
        </li>
        <li>
          <Strong>Kokumin</Strong> não tem nada parecido. Doença ou falta de
          trabalho = renda zero, exceto se você tem reserva.
        </li>
      </UL>

      <H3>Dependentes</H3>
      <UL>
        <li>
          <Strong>Shakai hoken</Strong> cobre cônjuge sem renda e filhos
          como dependentes <Em>sem custo adicional</Em>. Eles têm cartão de
          seguro emitido pela empresa.
        </li>
        <li>
          <Strong>Kokumin</Strong> cobra por pessoa. Cada membro da
          família tem que ser cadastrado separadamente — o valor sobe.
        </li>
      </UL>

      <H2 id="quando-muda">Quando você muda de um para outro</H2>

      <H3>Saiu do emprego (demissão ou pedido)</H3>
      <P>
        Você tem <Strong>14 dias</Strong> a contar da saída para se
        inscrever em kokumin no escritório da prefeitura. Não fazer isso é
        violação — e na hora de renovar visto, eles checam.
      </P>
      <P>
        Existe também a opção <Em>nin-i keizoku</Em> (任意継続): continuar
        no plano de saúde da empresa anterior por até 2 anos pagando você
        mesmo o valor cheio (dobro do que pagava antes). Útil se a
        diferença for grande contra o kokumin do seu município.
      </P>

      <H3>Entrou em emprego novo</H3>
      <P>
        Automaticamente passa a shakai hoken pela nova empresa. Mas
        atenção: você precisa <Strong>cancelar o kokumin</Strong> na
        prefeitura, ou continuará sendo cobrado. Leve o novo cartão de
        seguro à prefeitura para informar.
      </P>

      <H3>Trabalhou parte do mês em cada</H3>
      <P>
        O mês inteiro é contabilizado pelo seguro que você estava no{" "}
        <Strong>último dia do mês</Strong>. Então se você saiu da empresa
        dia 25 mas só entrou na nova dia 5 do mês seguinte, ficou kokumin
        aquele mês inteiro.
      </P>

      <H2 id="autonomo">Pensando em virar autônomo?</H2>
      <P>
        Trocar shakai por kokumin tem implicações importantes:
      </P>
      <UL>
        <li>
          Você paga sozinho — sem contraparte da empresa.
        </li>
        <li>
          Sua aposentadoria futura é só kokumin nenkin (limite ~¥66k/mês).
          Considere começar uma <Em>iDeCo</Em> (aposentadoria privada) ou{" "}
          <Em>kojin gata</Em> para complementar.
        </li>
        <li>
          Sem seguro-desemprego. Sem afastamento por doença remunerado.
        </li>
        <li>
          Você passa a ser obrigado a fazer{" "}
          <Link to="/guias/kakutei-shinkoku" className="text-accent underline">
            kakutei shinkoku
          </Link>{" "}
          todo ano.
        </li>
        <li>
          <Strong>Vantagem:</Strong> pode deduzir muito mais despesas como
          gasto de negócio. Casa, carro, internet, parte das contas — o que
          for relacionado ao trabalho. Bom contador pode reduzir
          significativamente sua carga tributária.
        </li>
      </UL>

      <H2 id="conclusao">Resumo prático</H2>
      <OL>
        <li>
          Se você é empregado formal: você tem shakai hoken,
          provavelmente é a melhor opção, mantenha.
        </li>
        <li>
          Se está em transição: cuidado com os 14 dias para se cadastrar
          no kokumin. Não dá pra ficar sem.
        </li>
        <li>
          Se está pensando em autônomo: faça as contas. Considere começar
          iDeCo para compensar a aposentadoria menor.
        </li>
        <li>
          Para o custo exato do kokumin na sua cidade, contate a
          prefeitura (役所). Eles dão estimativa precisa baseado na sua
          renda do ano anterior.
        </li>
      </OL>
    </GuideLayout>
  );
}
